import { inject, Injectable } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { BehaviorSubject, take } from 'rxjs';
import { API_URLS } from '@core/api-url.enum';
import { HttpService } from '@core/http.service';
import { environment } from '@environments/environment';
import {
  RemoteConfig,
  fetchAndActivate,
  getValue
} from '@angular/fire/remote-config';
import { REMOTE_CONFIG } from '@shared/constants/confirmation.constant';
import { ToasterService } from './toaster.service';
import { AuthService } from '@core/auth.service';
import { topFanD, topFanE } from '@utils/common.utils';
import { MobileBridgeService } from '@services/mobile-bridge.service';
import { MOBILE_BRIDGE_EVENT_RECEIVE, MOBILE_BRIDGE_EVENT_SEND } from '@constants/mobile-bridge.constant';
import { PlatformService } from '@core/services/platform.service';
@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private readonly _messaging = inject(Messaging);
  public $deviceId = new BehaviorSubject<string | null>(null);
  iosPayButtonsConfig = {
    payWithWalletCredits: true,
    payWithCreditCard: false
  };
  private token = '';
  private deviceId: string | null = null;
  constructor(
    private readonly httpService: HttpService,
    private readonly ts: ToasterService,
    private remoteConfig: RemoteConfig,
    private readonly mobileBridgeService: MobileBridgeService,
    private authService: AuthService,
    private platformService: PlatformService
  ) {}

  requestFirebaseToken() {
      if(this.mobileBridgeService.isMobileApp){
        this.mobileBridgeService.sendMessage(MOBILE_BRIDGE_EVENT_SEND.GET_FCM_TOKEN, {});
        this.mobileBridgeService.messageSubject.subscribe(message => {
          if (message.type === MOBILE_BRIDGE_EVENT_RECEIVE.DEVICE_NOTIFICATION_TOKEN && message.payload?.token) {
            this.setConfigToken(message.payload.token, message.payload?.isApp);
          }
        })
      }else{
        getToken(this._messaging, { vapidKey: environment.firebaseVapidKey })
        .then((token) => {
          this.setConfigToken(token, false);
        })
        .catch(() => {
          if (!environment.production) {
            console.log('Token error');
          }
        });
      }
 }
  get deviceToken(): string | null {
    return this.deviceId || null;
  }
  async generateFingerprint() {
    if (this.platformService.isBrowser) {
      try {
        const { default: FingerprintJS } = await import(
          '@fingerprintjs/fingerprintjs'
        );
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        this.deviceId = result.visitorId;
        this.$deviceId.next(this.deviceId);
      } catch (err) {
        this.deviceId = null;
      }
    }
  }

  onMessage(): void {
    onMessage(this._messaging, {
      next: (payload) => {
        if (payload?.data.description && payload?.data?.redirect_url) {
          if(payload?.data?.full_name){
            payload.data.description = `${payload?.data?.full_name}: ${payload?.data.description}`;  
          }
          if(payload?.data?.from_wls && payload?.data?.from_wls === 'true'){
            if(environment.websiteURL.includes(payload?.data?.wls_url)){
              this.ts.showActivityNotificationToast(
                payload?.data.description,
                payload?.data?.redirect_url
              , payload?.data?.icon);
            }
          }
          else if(!environment.whiteLabelConfig){
            this.ts.showActivityNotificationToast(
              payload?.data.description,
              payload?.data?.redirect_url
            );
          }
        }
      },
      error: () => {
        if (!environment.production) {
          console.log('Message error');
        }
      },
      complete: () => {
        if (!environment.production) {
          console.log('Done listening to messages');
        }
      }
    });
  }
  setToken(dataToSend: { token: string; isApp: boolean }) {
    this.httpService
      .httpRequest(`POST`, API_URLS.pushNotification, false, dataToSend)
      .pipe(take(1))
      .subscribe(() => {
        if (!dataToSend.isApp) {
          this.onMessage();
        }
      });
  }

  logOutToken() {
    if (this.authService.getUserIndexedDb() && (this.token || this.deviceToken)) {
      this.httpService
        .httpRequest(`PUT`, API_URLS.pushNotification, false, {
          token: this.token || null,
          device_id: this.deviceToken
        })
        .pipe(take(1))
        .subscribe();
    }
  }

  getRemoteConfigValue() {
    if ((this.platformService.isBrowser || this.platformService.isPrerendered) && this.remoteConfig) {
      this.remoteConfig.settings.minimumFetchIntervalMillis = 0;
      fetchAndActivate(this.remoteConfig)
        .then(() => {
          const iosButtonsConfig = getValue(
            this.remoteConfig,
            REMOTE_CONFIG.PAYMENT_CONFIG
          ).asString();

          const parsedConfig = JSON.parse(iosButtonsConfig);
          this.iosPayButtonsConfig = {
            payWithWalletCredits: parsedConfig.payWithWalletCredits,
            payWithCreditCard: parsedConfig.payWithCreditCard
          };
        })
        .catch((error) => {
          console.error('Error fetching remote config:', error);
        });
    }
  }

  setConfigToken(token: string, isApp = false) {
    this.token = token;
    const existingToken = topFanD(window.localStorage.getItem('fc_token') || '')[0];
    const existingIsApp = window.localStorage.getItem('fc_is_app') === 'true';
    const shouldRegisterToken = token !== existingToken || isApp !== existingIsApp;

    if (shouldRegisterToken) {
      window.localStorage.setItem('fc_token', topFanE(token, 'fc_token'));
      window.localStorage.setItem('fc_is_app', String(isApp));
      this.setToken({ token, isApp });
    }
  }
}
